import { inject as ngInject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';

import { appInject } from 'src/app/app-injector';
import { OWCC_QUERY_PARAM } from 'src/app/core/config/query-params.config';
import { OWCC_SESSION_STORAGE_KEY } from 'src/app/core/config/web-storage.config';
import { RouterEventsService } from 'src/app/core/services/router-events/router-events.service';
import { SessionStorageService } from 'src/app/core/services/storage/session-storage/session-storage.service';
import { noop } from 'src/app/utils/functions/noop';
import { filterTruthy } from 'src/app/utils/functions/rx-filters';

import { selectIsInIntegratedMode } from '../../common/common.selectors';

/**
 * Updates URL (once - when entering OWCC) if a sales touch 'source' value is stored in the session storage.
 * Sales Touch is only available for integrated mode.
 *
 * Needed because a 'Sales Touch' person might correctly jump in via query parameter 'source=stouch' (which then shows the corresponding NBAs) but then leaves OWCC.
 * When she/he jumps back into OWCC the (in the session storage) previously stored value gets re-attached as a query parameter to again show the correct configuration.
 */
export const updateUrlIfSalesTouchSourceValueInSessionStorage$ = createEffect(
  () =>
    combineLatest([
      ngInject<Store>(Store).select(selectIsInIntegratedMode),
      ngInject<RouterEventsService>(RouterEventsService).navigationEnd$,
      ngInject<ActivatedRoute>(ActivatedRoute).queryParams
    ]).pipe(
      filter(([isIntegrated, , queryParams]) => !!isIntegrated && !queryParams[OWCC_QUERY_PARAM.SALES_TOUCH_SOURCE]),
      map(([, navigationEnd]) => navigationEnd.urlAfterRedirects),
      distinctUntilChanged(),
      map(() =>
        appInject<SessionStorageService>(SessionStorageService).get(OWCC_SESSION_STORAGE_KEY.SALES_TOUCH_SOURCE)
      ),
      filterTruthy(),
      tap(salesTouchSourceValue => {
        appInject<Router>(Router)
          .navigate([], {
            relativeTo: appInject<ActivatedRoute>(ActivatedRoute),
            queryParams: {
              source: salesTouchSourceValue
            },
            queryParamsHandling: 'merge'
          })
          .catch(noop);
      }),
      take(1)
    ),
  { functional: true, dispatch: false }
);

/**
 * Updates the session store with the 'sales touch' source parameter.
 *
 * Needed because a 'Sales Touch' person might correctly jump in via query parameter 'source=stouch' (which then shows the corresponding NBAs) but then leaves OWCC.
 * When she/he jumps back into OWCC the (in the session storage) previously stored value gets re-attached as a query parameter to again show the correct configuration.
 */
export const updateSessionStorageBySalesTouchSourceQueryParameter$ = createEffect(
  () =>
    combineLatest([
      ngInject(Store).select(selectIsInIntegratedMode),
      ngInject(RouterEventsService).navigationEnd$
    ]).pipe(
      filter(([isIntegrated]) => !!isIntegrated),
      map(([, navigationEnd]) => navigationEnd.urlAfterRedirects),
      distinctUntilChanged(),
      switchMap(() =>
        appInject<ActivatedRoute>(ActivatedRoute).queryParams.pipe(
          filter(params => !!(params[OWCC_QUERY_PARAM.SALES_TOUCH_SOURCE] as string | undefined)),
          map(params => params[OWCC_QUERY_PARAM.SALES_TOUCH_SOURCE] as string)
        )
      ),
      tap(salesTouchSourceValue => {
        appInject<SessionStorageService>(SessionStorageService).set(
          OWCC_SESSION_STORAGE_KEY.SALES_TOUCH_SOURCE,
          salesTouchSourceValue
        );
      }),
      take(1)
    ),
  { functional: true, dispatch: false }
);
